import { Component, OnInit, inject, computed, Signal, WritableSignal, signal, effect, untracked } from '@angular/core';
import { Location } from '@angular/common';
import {
  BalanceQuery,
  config,
  ErrorStateService,
  MemberDetails,
  MemberQuery,
  CardTokenQuery,
  PaymentService,
  OrderItem,
  CheckoutService,
  MemberService,
  DISABLED_STATUSES,
  CartV2Service,
  MarketplaceFacade,
  MarketplaceV2Service,
} from '@fgb/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  UntypedFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AddPaymentCardDetailComponent } from '@fgb/portal-component-library/src/lib/loyalty/add-payment-card-detail';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';

@Component({
  selector: 'fgb-cart-checkout-page',
  templateUrl: './cart-checkout-page.component.html',
  styleUrls: ['./cart-checkout-page.component.scss'],
})
export class CartCheckoutPageComponent implements OnInit {
  hasSubmittedMemberAddress: boolean = false;

  private checkoutService = inject(CheckoutService);
  private errorService = inject(ErrorStateService);
  private fb: FormBuilder = inject(UntypedFormBuilder);
  private memberService = inject(MemberService);
  private location = inject(Location);
  private memberQuery = inject(MemberQuery);
  private balanceQuery = inject(BalanceQuery);
  private cardTokenQuery = inject(CardTokenQuery);
  private marketplaceService = inject(MarketplaceV2Service);
  private paymentService = inject(PaymentService);
  private cartService = inject(CartV2Service);
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private marketplaceFacade = inject(MarketplaceFacade);

  checkoutForm: FormGroup;
  loading: boolean = false;
  private _isInitialized = false;
  config: any = config;
  orderId: string = '';
  saveAsCardToken: boolean = false;
  modalRef: NgbModalRef;
  editingMemberAddress: boolean = false;
  readonly checkoutSummary = this.checkoutService.summary;

  readonly items = this.checkoutService.items;

  readonly memberDetails = toSignal(this.memberQuery.selectMemberDetails());
  readonly currentBalance = toSignal(this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty));
  readonly cardTokens = toSignal(this.cardTokenQuery.selectUnfrozenCards(), { initialValue: [] });

  readonly cardInfo = computed(() => (this.cardTokens().length > 0 ? { ...this.cardTokens()[0] } : null));
  readonly useCardToken = computed(() => !!this.cardInfo()?.CardNumber);
  readonly portalId = computed(() => this.memberDetails()?.PortalId || '');
  readonly orderBody = computed(() =>
    this.items().map((item: any) => ({
      Id: item.productData.Id,
      RewardItemId: item.productData.ProductItemId,
      GameNumber: item.productData.GameNumber,
      Quantity: item.purchaseQuantity,
      CustomFields: item.customFields || [],
      AttributeMappingId: item.attributes?.AttributeMappingId || null,
    }))
  );
  orderPreview: WritableSignal<any> = signal(null);

  constructor() {
    effect(
      () => {
        const items = this.items();
        if (this._isInitialized && items?.length > 0) {
          untracked(() => {
            this.errorService.clearErrors();
            this._init();
          });
        }
      },
      { allowSignalWrites: true }
    );
  }

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnInit() {
    this.errorService.clearErrors();
    this._init();
    this._isInitialized = true;
  }

  /* ================================
   * Public Methods
   * ================================ */

  /** Navigate back to the previous page in history */
  goBack() {
    this.location.back();
  }

  /** Pay with existing card token */
  payNow(): void {
    if (this.loading) return;
    this.checkoutForm.markAllAsTouched();

    if (this.checkoutForm.valid) {
      this.loading = true;

      this.marketplaceService
        .completePurchaseBasketOrder(
          this.orderId,
          '/checkout',
          '/rewards/marketplace/purchase/success',
          this.useCardToken(),
          false
        )
        .pipe(
          finalize(() => {
            this.loading = false;
            this.marketplaceFacade.clearCache();
          })
        )
        .subscribe({
          next: (response) => {
            const orderId = response?.Data?.OrderId || '';
            if (orderId) {
              this.router.navigate([`rewards/marketplace/purchase/success`], { queryParams: { purchaseOrderId: orderId } });
              this.clearAll();
            }
          },
        });
    }
  }

  /** Checkout with new card */
  checkout(saveAsCardToken: boolean = false): void {
    this.marketplaceService
      .completePurchaseBasketOrder(
        this.orderId,
        this.config.portalUrl + '/ecash/failure',
        this.config.portalUrl + '/checkout/results/',
        false,
        saveAsCardToken
      )
      .subscribe({
        next: (response) => {
          const orders = this.createOrders(this.items());
          const order = this.paymentService.createOrder(orders);
          this.paymentService.createPayment(
            order,
            response.Data,
            'payment-gateway-area',
            '/checkout/results',
            '/rewards/marketplace/purchase/success',
            '/portalproducts/failure'
          );
        },
      });
  }

  /** Start editing the user's member details address. */
  editMemberAddress() {
    this.editingMemberAddress = true;
  }

  /** Clear the member details address form fields. */
  clearAddressForm() {
    this.checkoutForm.get('address')?.patchValue({
      Street: '',
      Town: '',
      County: '',
      PostCode: '',
      Country: '',
      HomeNumber: '',
    });
  }

  /** Cancel editing the member details address form and rebuild it with the original data. */
  cancelAndRebuildForm() {
    this.editingMemberAddress = false;
    this.checkoutForm = this._initializeForm();
  }

  /** Save the member details address form, updating both the member details and the marketplace order with the new address. */
  saveMemberDetailAddress() {
    this.hasSubmittedMemberAddress = true;

    this.checkoutForm.get('address')?.markAllAsTouched();

    if (this.checkoutForm.get('address')?.valid) {
      const memberDetails = { ...this.memberDetails(), ...this.checkoutForm.get('address')?.value };

      this.memberService.updateMemberDetails(this.portalId(), memberDetails);
      this.marketplaceFacade.updateAddressByOrderId(this.orderId, memberDetails).subscribe({
        next: () => {
          this.editingMemberAddress = false;
          this.errorService.clearErrors();
        },
      });
    }
  }

  /**
   * Open the add payment detail modal, passing in the current address form value and a callback to trigger checkout after the user submits their payment details in the modal.
   */
  openCartAddPaymentDetailModal(saveAsCardToken: boolean = false) {
    const form = this.checkoutForm.get('address');
    this.modalRef = this.modalService.open(AddPaymentCardDetailComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
    });
    this.modalRef.componentInstance.formValue = form;
    this.modalRef.componentInstance.checkoutMode = true;
    this.modalRef.componentInstance.closeModal = this.closeModal.bind(this);
    this.modalRef.componentInstance.redirectUrl = '/checkout/results/';
    this.modalRef.componentInstance['closeModalEvent'].subscribe(() => {
      this.closeModal();
    });
    this.modalRef.componentInstance['submittedAddressEvent'].subscribe(() => {
      this.checkout(saveAsCardToken); //< you now have access to the event that was emitted, to pass to your grandfather component.
    });
  }

  /**
   * Close the add payment detail modal and destroy the payment component to clean up any iframes or listeners it may have created.    */
  closeModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.paymentService.destroyPaymentComponent();
    }
  }

  /* ================================
   * Private Helpers
   * ================================ */

  /** Initialize the checkout process */
  private _init(): void {
    if (!this.checkoutService.items()) {
      return;
    }

    this.marketplaceFacade.checkoutStart(this.orderBody(), this.memberDetails()).subscribe({
      next: (response) => {
        this.orderId = response.OrderId;
        this.orderPreview.set(response);
      },
    });
    this.checkoutForm = this._initializeForm();
  }

  /** Initialize the checkout form */
  private _initializeForm(): FormGroup {
    return this.fb.group(
      {
        address: this._initializeAddressForm(this.memberDetails()),
      },
      {
        validators: [
          checkoutFormValidator,
          balanceValidator(this.currentBalance, this.checkoutSummary),
          itemsValidator(this.items),
        ],
      }
    );
  }

  /** Initialize the address form */
  private _initializeAddressForm(md: MemberDetails | undefined): FormGroup {
    return this.fb.group({
      AddressName: [md?.AddressName || ''],
      Street: [md?.Street || '', Validators.required],
      Town: [md?.Town || '', Validators.required],
      County: [md?.County || '', Validators.required],
      Country: [md?.Country || '', Validators.required],
      PostCode: [md?.PostCode || '', Validators.required],
      HomeNumber: [md?.HomeNumber || '', Validators.required],
    });
  }

  /** Create order items */
  private createOrders(items: any[]): OrderItem[] {
    return (
      items?.map(
        (item: any) =>
          new OrderItem(
            item.productData.Id,
            item.productData.DisplayName,
            item.purchaseQuantity,
            item.productData.MerchantValue,
            item.productData.ItemImageURL
          )
      ) || []
    );
  }

  private clearAll(): void {
    // If there are more than 1 items in the checkout items clear the cart.
    // This means it's been checked out from the cart.
    if (this.items()?.length > 1) {
      this.cartService.removeAll();
    }
    this.checkoutService.clearAll();
  }
}

function checkoutFormValidator(form: AbstractControl): ValidationErrors | null {
  const address = form.get('address');
  if (!address?.valid) {
    return { invalidAddress: true };
  }
  return null;
}

function balanceValidator(balance: Signal<number | undefined>, checkoutSummary: Signal<any>): ValidatorFn {
  return (): ValidationErrors | null =>
    (balance() ?? 0) < checkoutSummary().pointsItems.totalPoints ? { insufficientBalance: true } : null;
}

function itemsValidator(items: Signal<any[]>): ValidatorFn {
  return (): ValidationErrors | null => {
    const hasDisabledItem = items().some((item) => DISABLED_STATUSES.has(item.productData.Status));
    return hasDisabledItem ? { hasDisabledItems: true } : null;
  };
}
